import React from 'react';
import { LoadingSpinner } from '../../LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { useAccreditationApplications } from '../../../hooks/useAccreditationApplications';
import { IAccreditationApplication } from 'interfaces/IAccreditationApplication';
import IApplicationReview from 'interfaces/IApplicationReview';
import { LockOwner } from '../../../graphql/__generated__/graphql';
import { useAccreditations } from 'hooks/useAccreditations';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import AccreditationsBlockList from './AccreditationsBlockList';
import ApplicationsBlockList from './ApplicationsBlockList';

export type ApplicationAction = 'apply' | 'view' | 'renew';

const ApplicationsBlock: React.FC = () => {
    const notifications = useNotificationContext();
    const navigate = useNavigate();

    const {
        models: { loading: applicationsLoading, updating, accreditationTypes, applications },
        operations,
    } = useAccreditationApplications();
    const {
        models: {
            accreditations: { active, expired, renewable },
            loading: accreditationsLoading,
        },
    } = useAccreditations();

    // useEffect(() => {
    //     operations.fetchApplications().catch((e) => {
    //         console.error('Something went wrong retrieving applications', e);
    //         notifications.error('Something went wrong whilst retrieving your applications.');
    //     });
    // }, []);

    if (applicationsLoading || accreditationsLoading) return <LoadingSpinner />;

    const handleOnSelectApplication = (
        application: IAccreditationApplication,
        action: ApplicationAction
    ) => {
        navigate('/accreditation/' + action, { state: { application } });
    };

    const handleOnAbandonApplication = async (application: IAccreditationApplication) => {
        operations
            .deleteApplication(application)
            .then(() => {
                notifications.success('Application abandoned successfully');
            })
            .catch((e) => {
                console.error('Something went wrong abandoning application', e);
                notifications.error('Something went wrong whilst abandoning the application.');
            });
    };

    const handleOnSubmitForReview = async (application: IAccreditationApplication) => {
        return operations
            .submitApplication(application, LockOwner.Supervisor)
            .then(() => {
                navigate('/accreditation?formSubmitType=supervisor', { replace: false });
                return this;
            })
            .catch(() => {
                notifications.error(
                    'Something went wrong when submitting the application. Please try again later'
                );
                operations.fetchApplications();
            });
    };

    const handleOnSubmitFinalApplication = async (application: IAccreditationApplication) => {
        operations
            .submitApplication(application, LockOwner.Emdr)
            .then(() => {
                notifications.success('Application successfully submitted to the EMDR committee.');
                navigate('/accreditation?formSubmitType=emdr', { replace: false });
            })
            .catch((e) => {
                console.error('Something went wrong submitting application', e);
                notifications.error('Something went wrong when submitting your application.');
            });
    };

    const handleOnSelectReview = (
        appRev: IApplicationReview,
        application: IAccreditationApplication
    ) =>
        navigate('/accreditation/review', {
            state: {
                review: {
                    ...appRev,
                    accreditationType: application.accreditationType,
                },
            },
        });

    const renewalApplications: IAccreditationApplication[] = [];
    const mergedApplications: IAccreditationApplication[] = [];

    accreditationTypes.forEach((accreditationType) => {
        if (renewable.includes(accreditationType.accreditationTypeId)) {
            // Conditional check to satisfy TypeScript thinking it might not exist
            const application = {
                ...(applications.find((app) => {
                    return (
                        app.isRenewal &&
                        app.accreditationType.accreditationTypeId ===
                            accreditationType.accreditationTypeId
                    );
                }) ?? {
                    jsonData: '{}',
                    lockedBy: LockOwner.Applicant,
                    hasMitigation: false,
                    isRenewal: true,
                }),
            };
            renewalApplications.push({
                ...application,
                accreditationType,
            });
        } else {
            const application = {
                ...(applications.find(
                    (app) =>
                        app.accreditationType.accreditationTypeId ===
                        accreditationType.accreditationTypeId
                ) ?? {
                    jsonData: '{}',
                    lockedBy: LockOwner.Applicant,
                    hasMitigation: false,
                    isRenewal: false,
                }),
            };
            mergedApplications.push({ ...application, accreditationType: accreditationType });
        }
    });

    return (
        <>
            {expired?.length > 0 && (
                <AccreditationsBlockList
                    accreditations={expired}
                    applications={renewalApplications}
                    header={'Expired Accreditations'}
                    onSelectApplication={handleOnSelectApplication}
                    onSubmitFinalApplication={handleOnSubmitFinalApplication}
                    onSubmitForReview={handleOnSubmitForReview}
                />
            )}
            {active?.length > 0 && (
                <AccreditationsBlockList
                    accreditations={active}
                    applications={renewalApplications}
                    header={'Active Accreditations'}
                    onSelectApplication={handleOnSelectApplication}
                    onSubmitFinalApplication={handleOnSubmitFinalApplication}
                    onSubmitForReview={handleOnSubmitForReview}
                />
            )}

            <ApplicationsBlockList
                header={active.length > 0 ? 'Add extra accreditations' : 'Apply for accreditation'}
                applications={mergedApplications}
                onSelectApplication={handleOnSelectApplication}
                onSelectReview={handleOnSelectReview}
                onAbandonApplication={handleOnAbandonApplication}
                onSubmitFinalApplication={handleOnSubmitFinalApplication}
                onSubmitForReview={handleOnSubmitForReview}
                updating={updating}
            />
        </>
    );
};

export default ApplicationsBlock;
